/**
 * Helpers for a native `<input type="number">` that is kept in step with a stored number.
 *
 * The trap they exist for: refilling such a field from the stored number after every keystroke
 * overwrites text that is not how the number is normally written. "0,0" is the number 0, which is
 * written "0", so typing 0,05 left "0" behind and then "5" — 12,05 became 125. A field must only be
 * refilled when the number it shows is a different number (Vue's own `v-model` has this guard for
 * number inputs; hand-written `:value` + `@input` pairs do not).
 */

/**
 * The number the field shows. `null` while it is empty or holds incomplete text such as "-" or
 * "0," — the browser reports both as an empty value.
 */
export function shownNumber(input: HTMLInputElement): number | null {
  if (input.value === '') return null
  const parsed = parseFloat(input.value)
  return Number.isNaN(parsed) ? null : parsed
}

/**
 * Whether the field already shows `value`, so it must be left alone. `emptyIsZero` is for a field
 * whose stored value cannot be empty and reads 0 when the text is: it then counts as showing 0.
 */
export function isShowingNumber(
  input: HTMLInputElement,
  value: number | null,
  options: { emptyIsZero?: boolean } = {}
): boolean {
  const shown = shownNumber(input)
  return (shown ?? (options.emptyIsZero ? 0 : null)) === value
}

/** Puts a stored number into the field as plain text; `null` empties it. */
export function writeNumber(input: HTMLInputElement, value: number | null): void {
  input.value = value === null ? '' : String(value)
}
